// Mount-only: scans `user.achievements` against `user.celebrated_runes`
// each time the auth user refreshes, fires a one-off toast for any rune
// the walker holds but hasn't been celebrated for yet, then POSTs the
// mark so it never fires again across devices. First-rollout backfill
// (db.js M-3) ensures existing walkers never see a flood.
//
// Derived values declared before the hooks/handlers that use them, per
// the project's hook-ordering rule.
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { celebrateRune } from "../lib/achievementUX";

export default function RuneCelebrator() {
  const { user } = useAuth();
  const navigate = useNavigate();
  // Prevent re-firing within a single page lifetime even if auth refresh
  // temporarily lags behind the server state we just updated.
  const firedRef = useRef(new Set());

  const achievements = Array.isArray(user?.achievements) ? user.achievements : [];
  const celebrated = Array.isArray(user?.celebrated_runes) ? user.celebrated_runes : [];
  const unseen = achievements.filter(
    (k) => k && !celebrated.includes(k) && !firedRef.current.has(k),
  );

  useEffect(() => {
    if (!user) return;
    if (unseen.length === 0) return;
    (async () => {
      // Space the toasts out a touch when multiple runes land at once
      // (unlikely in practice; keeps the UI calm just in case).
      for (let i = 0; i < unseen.length; i++) {
        const k = unseen[i];
        firedRef.current.add(k);
        setTimeout(() => celebrateRune(navigate, k), i * 700);
        try {
          await api.post("/me/celebrate-rune", { rune: k });
        } catch {
          // Non-blocking — if the mark fails the toast will re-fire on
          // next load, which is acceptable for a nice-to-have signal.
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id, achievements.join("|"), celebrated.join("|")]);

  return null;
}
